import {Component, DestroyRef, OnDestroy, ViewChild, inject} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {MatCard, MatCardContent} from '@angular/material/card';
import {MatButton} from '@angular/material/button';
import {MatFormField, MatLabel} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {ActivatedRoute} from '@angular/router';
import {HttpErrorResponse} from '@angular/common/http';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {TranslocoModule} from '@jsverse/transloco';
import {CodeEntryComponent} from '../../../../../shared/ui/code-entry/code-entry.component';
import {ALL_MFA_METHODS, MfaMethod, MfaService, usableMethods} from './mfa.service';
import {StepUpContext, readStepUpContext, resumeUrl, siblingUrl, signInUrl} from './step-up-context';
import {MFA_CODE_LENGTH} from './mfa-challenge.component';

const RESEND_COOLDOWN_SEC = 60;

type EnrollState = 'loading' | 'prove' | 'choose' | 'confirm' | 'codes' | 'redirecting' | 'blocked' | 'error';

/**
 * MfaEnrollComponent — the platform-default enrollment interstitial (Story
 * 47.2, AC2/AC6). Reached post-password when an MFA-required application is hit
 * by a user who has not enrolled a second factor. Enrollment is per user, per
 * environment, exactly once: the same authenticator-app / email-code choice as
 * the operator console (47.1), confirm-before-enable, and the one-time recovery
 * codes shown once. Confirming elevates the session, so after "I've saved them"
 * the page resumes straight into the interrupted flow.
 */
@Component({
  selector: 'app-mfa-enroll',
  standalone: true,
  imports: [
    FormsModule,
    MatCard, MatCardContent,
    MatButton,
    MatFormField, MatLabel, MatInput,
    MatProgressSpinnerModule,
    TranslocoModule,
    CodeEntryComponent,
  ],
  templateUrl: './mfa-enroll.component.html',
  styleUrls: ['./mfa-page.css', './mfa-enroll.component.css'],
})
export class MfaEnrollComponent implements OnDestroy {
  state: EnrollState = 'loading';
  method: MfaMethod | '' = '';
  /** Story 47.6 — what THIS application accepts (both when unrestricted). */
  allowed: MfaMethod[] = ALL_MFA_METHODS;
  /** Story 47.6 — what the user already holds. */
  held: MfaMethod[] = [];
  /** Story 47.6 prove-then-add — the held method the user proves before adding one. */
  proveMethod: MfaMethod | '' = '';
  useRecovery = false;
  recoveryCode = '';
  qrDataUrl = '';
  secret = '';
  recoveryCodes: string[] = [];
  code = '';
  submitting = false;
  resending = false;
  starting = false;
  error: string | null = null;
  notice: string | null = null;
  resendIn = 0;
  readonly codeLength = MFA_CODE_LENGTH;

  @ViewChild(CodeEntryComponent) private entry?: CodeEntryComponent;

  private ctx!: StepUpContext;
  private resendTimer: ReturnType<typeof setInterval> | null = null;

  private readonly service = inject(MfaService);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.route.queryParams.pipe(takeUntilDestroyed()).subscribe(params => {
      this.ctx = readStepUpContext(params);
      // Story 53.9: a missing session surfaces as /mfa/state's 401.
      if (!this.ctx.clientId) {
        this.goSignIn();
        return;
      }
      this.loadState();
    });
  }

  ngOnDestroy(): void {
    this.stopCooldown();
  }

  get isEmail(): boolean {
    return this.method === 'email';
  }

  get isProveEmail(): boolean {
    return this.proveMethod === 'email';
  }

  /** The methods this application accepts that the user does not yet hold. */
  get chooseOptions(): MfaMethod[] {
    return this.allowed.filter(m => !this.held.includes(m));
  }

  canChoose(m: MfaMethod): boolean {
    return this.chooseOptions.includes(m);
  }

  get canProve(): boolean {
    if (this.submitting) return false;
    return this.useRecovery ? this.recoveryCode.trim().length >= 10 : this.code.length === MFA_CODE_LENGTH;
  }

  /** The manual key in groups of four for readability. */
  get secretGroups(): string {
    return this.secret.replace(/(.{4})/g, '$1 ').trim();
  }

  get canConfirm(): boolean {
    return !this.submitting && this.code.length === MFA_CODE_LENGTH;
  }

  private loadState(): void {
    this.state = 'loading';
    this.service.state(this.ctx)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: s => {
          // Resume on SATISFIED for this client (Story 47.6); older servers send
          // only `elevated`, which means the same when nothing is restricted.
          if (s.satisfied ?? s.elevated) {
            this.resume();
            return;
          }
          this.allowed = s.allowed?.length ? s.allowed : ALL_MFA_METHODS;
          this.held = s.methods?.length ? s.methods : (s.method ? [s.method] : []);
          if (s.enrolled) {
            if (usableMethods(s).length > 0) {
              // Holds something this application accepts → the challenge page owns this.
              this.state = 'redirecting';
              this.navigate(siblingUrl(this.ctx, '/mfa/challenge'));
              return;
            }
            // Story 47.6 AC3 — enrolled, but nothing this application accepts.
            // Adding a method needs an elevated session (47.5's takeover guard):
            // if this session is already elevated (the prove step just happened,
            // or another application's challenge did), offer the add straight
            // away; otherwise have the user prove a method they hold first.
            if (s.elevated) {
              this.state = 'choose';
              return;
            }
            this.proveMethod = this.held.includes('totp') ? 'totp' : (this.held[0] ?? '');
            this.state = 'prove';
            if (this.isProveEmail) {
              this.sendProveCode(true);
            }
            return;
          }
          this.state = 'choose';
        },
        error: (err: HttpErrorResponse) => this.applyError(err),
      });
  }

  choose(method: MfaMethod): void {
    if (this.starting) return;
    this.starting = true;
    this.error = null;
    this.service.enrollStart(this.ctx, method)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: resp => {
          this.starting = false;
          this.method = method;
          this.qrDataUrl = resp.qr_png ?? '';
          this.secret = resp.secret ?? '';
          this.code = '';
          if (method === 'email') {
            this.notice = resp.code_sent ? 'mfa.enroll.emailSent' : 'mfa.enroll.emailSentRecently';
            this.startCooldown();
          } else {
            this.notice = null;
          }
          this.state = 'confirm';
        },
        error: (err: HttpErrorResponse) => {
          this.starting = false;
          if (err.status === 400 && (err.error as {error?: string} | null)?.error === 'invalid_request') {
            // Story 47.6: this application does not accept that method (the
            // option is normally hidden; a stale page can still race the operator).
            this.error = 'mfa.enroll.methodNotAllowed';
            return;
          }
          this.applyError(err);
        },
      });
  }

  // ─── Story 47.6 prove-then-add ──────────────────────────────────────────────

  toggleRecovery(): void {
    this.useRecovery = !this.useRecovery;
    this.error = null;
    this.code = '';
  }

  /** Verify a HELD (but here rejected) method: elevates the session so the add is authorised. */
  prove(): void {
    if (!this.canProve) return;
    this.submitting = true;
    this.error = null;
    const factor = this.useRecovery ? {recoveryCode: this.recoveryCode} : {code: this.code};
    this.service.verify(this.ctx, factor)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.submitting = false;
          this.stopCooldown();
          this.code = '';
          this.recoveryCode = '';
          this.useRecovery = false;
          this.notice = null;
          this.state = 'choose';
        },
        error: (err: HttpErrorResponse) => {
          this.submitting = false;
          this.entry?.reset();
          this.code = '';
          const envelope = (err.error ?? {}) as {error?: string};
          if (err.status === 400 && envelope.error === 'invalid_grant') {
            this.error = this.useRecovery ? 'mfa.challenge.wrongRecovery' : 'mfa.challenge.wrongCode';
            return;
          }
          this.applyError(err);
        },
      });
  }

  proveResend(): void {
    this.sendProveCode(false);
  }

  private sendProveCode(initial: boolean): void {
    if (this.resending || (!initial && (this.resendIn > 0 || this.submitting))) return;
    this.resending = true;
    this.service.sendCode(this.ctx)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: resp => {
          this.resending = false;
          this.notice = resp.code_sent ? 'mfa.challenge.codeSent' : 'mfa.challenge.codeSentRecently';
          this.startCooldown();
        },
        error: (err: HttpErrorResponse) => {
          this.resending = false;
          this.startCooldown();
          this.applyError(err);
        },
      });
  }

  chooseAnother(): void {
    this.stopCooldown();
    this.method = '';
    this.qrDataUrl = '';
    this.secret = '';
    this.code = '';
    this.error = null;
    this.notice = null;
    this.state = 'choose';
  }

  onCodeChange(value: string): void {
    this.code = value;
    this.error = null;
  }

  onCodeCompleted(value: string): void {
    this.code = value;
    if (this.state === 'prove') {
      if (!this.useRecovery) this.prove();
      return;
    }
    this.confirm();
  }

  onSubmit(event: Event): void {
    event.preventDefault();
    if (this.state === 'prove') {
      this.prove();
      return;
    }
    this.confirm();
  }

  confirm(): void {
    if (!this.canConfirm) return;
    this.submitting = true;
    this.error = null;
    this.service.enrollConfirm(this.ctx, this.code)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: resp => {
          this.submitting = false;
          this.stopCooldown();
          const codes = resp.recovery_codes ?? [];
          if (!codes.length) {
            // An ADD (47.6): the account already holds its one recovery set
            // (47.5 AC2), so the server correctly returns none. Showing "Save
            // your recovery codes" with a genuinely empty list here would be
            // misleading — and Download would write an empty file. Resume
            // straight through instead.
            this.resume();
            return;
          }
          this.recoveryCodes = codes;
          this.state = 'codes';
        },
        error: (err: HttpErrorResponse) => {
          this.submitting = false;
          this.entry?.reset();
          this.code = '';
          this.applyError(err);
        },
      });
  }

  resend(): void {
    if (this.resending || this.resendIn > 0 || this.submitting) return;
    this.resending = true;
    this.service.enrollResend(this.ctx)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: resp => {
          this.resending = false;
          this.notice = resp.code_sent ? 'mfa.enroll.emailSent' : 'mfa.enroll.emailSentRecently';
          this.startCooldown();
        },
        error: (err: HttpErrorResponse) => {
          this.resending = false;
          this.startCooldown();
          this.applyError(err);
        },
      });
  }

  /** "I've saved my recovery codes" — the confirm already elevated the session. */
  codesCopied = false;

  /** Clipboard write can reject (permissions, insecure context) — never throw. */
  copyCodes(): void {
    void navigator.clipboard?.writeText(this.recoveryCodes.join('\n')).then(
      () => { this.codesCopied = true; },
      () => { this.codesCopied = false; },
    );
  }

  /** A Blob download needs no server round-trip and no new endpoint. */
  downloadCodes(): void {
    const blob = new Blob([this.recoveryCodes.join('\n') + '\n'], {type: 'text/plain'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  }

  continue(): void {
    this.resume();
  }

  backToSignIn(): void {
    this.goSignIn();
  }

  private resume(): void {
    this.state = 'redirecting';
    this.navigate(resumeUrl(this.ctx));
  }

  private goSignIn(): void {
    this.state = 'redirecting';
    this.navigate(signInUrl(this.ctx));
  }

  private applyError(err: HttpErrorResponse): void {
    const envelope = (err.error ?? {}) as {error?: string};
    if (err.status === 401) {
      this.goSignIn();
      return;
    }
    if (err.status === 409) {
      // A factor got enrolled elsewhere in the meantime → challenge instead.
      this.state = 'redirecting';
      this.navigate(siblingUrl(this.ctx, '/mfa/challenge'));
      return;
    }
    if (err.status === 403) {
      this.state = 'blocked';
      return;
    }
    switch (envelope.error) {
      case 'invalid_grant':
        this.error = 'mfa.enroll.wrongCode';
        return;
      case 'invalid_request':
        this.error = 'mfa.challenge.checkCode';
        return;
      default:
        if (this.state === 'loading') {
          this.state = 'error';
        } else {
          this.error = 'mfa.challenge.genericError';
        }
    }
  }

  private startCooldown(): void {
    this.stopCooldown();
    this.resendIn = RESEND_COOLDOWN_SEC;
    this.resendTimer = setInterval(() => {
      this.resendIn = Math.max(0, this.resendIn - 1);
      if (this.resendIn === 0) this.stopCooldown();
    }, 1000);
  }

  private stopCooldown(): void {
    if (this.resendTimer) {
      clearInterval(this.resendTimer);
      this.resendTimer = null;
    }
  }

  protected navigate(url: string): void {
    window.location.href = url;
  }
}
